import React from 'react';
import { graphql } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import Layout from '~/components/Layout';
import Seo from '~/components/SEO';
import TextCard from '~/components/TextCard';

import TwoColumns from '~/components/TwoColumns';
import TeamSlider from '~/components/TeamSlider';
import PressPreview from '~/components/PressPreview';
import CompanyBottomNav from '~/components/CompanyBottomNav';
import usePressPostPreviews from '~/hooks/usePressPostPreviews';
import useSpanHeading from '~/hooks/useSpanHeading';
import useStoryblok from '~/utils/storyblok-service';

import Image from '~/components/Dynamic/Image';
import RichTextRenderer from '~/components/Dynamic/RichTextRenderer';

const AboutPage = ({ data, location, pageContext }) => {
  const story = useStoryblok(data.storyblokEntry, location);
  const content = story.content;
  const pressPreviews = usePressPostPreviews().slice(0, 4);

  const {
    heroBottomHeading,
    heroHeading,
    heroImage,
    careers,
    impactColumns,
    impactText,
    introImages,
    introTextBottom,
    introTextTop,
    manifesto,
    pressText,
    teamHeading,
    teamMembers,
    buttons
  } = content;

  return (
    <Layout locale={pageContext.locale}>
      <Seo {...content.seo} lang={story.lang} pageContext={pageContext} />

      {/* Hero */}
      <section className="my-16 md:my-32">
        <div className="container">
          <h1 className="max-w-4xl text-h1 !font-medium md:text-52px xl:text-72px">{heroHeading}</h1>
          <div className="mt-16 md:mt-20">
            <Image blok={heroImage} />
          </div>
          <h2
            className="tracking-normal my-16 md:my-28 !leading-tight text-32px font-heading !font-normal md:text-52px xl:text-72px xl:pr-24"
            dangerouslySetInnerHTML={{ __html: useSpanHeading(heroBottomHeading) }}
          />
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 my-20 text-white bg-black md:my-28 md:py-28">
        <div className="container">
          <TextCard
            {...introTextTop[0]}
            className="max-w-2xl mb-12 md:mb-16 lg:pr-16"
            tag="h3"
            contentClass="opacity-90"
          />
          <div className="flex-wrap -ml-6 md:flex">
            <div className="flex items-end pl-6 md:flex-col md:w-5/12">
              <Image blok={introImages[0]} className="w-full h-auto mb-6" />
              <Image blok={introImages[1]} className="w-full mb-6 ml-6 md;ml-auto md:mb-0 md:w-10/12" align="ml-auto" />
            </div>
            <div className="pl-6 md:w-7/12">
              <Image blok={introImages[2]} />
              <div className="max-w-xl mx-auto mt-12 md:mt-16 opacity-90 md:px-8">
                <div className="prose">
                  <RichTextRenderer content={introTextBottom} className="prose" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="my-20 bg-white md:my-28">
        <div className="container">
          <TwoColumns
            text={
              <TextCard className="max-w-lg" contentClass="text-20px leading-relaxed " {...manifesto[0].content[0]} />
            }
            image={
              <div className="flex-auto max-w-sm mx-auto">
                <Image blok={manifesto[0]?.content[1]?.image} />
              </div>
            }
          />
        </div>
      </section>

      <section className="py-16 my-20 md:my-28 md:py-28 bg-lime">
        <div className="container max-w-4xl">
          <TextCard className="items-center max-w-lg mx-auto text-center md:my-4" {...careers[0]} />
        </div>
      </section>

      <section className="my-20 overflow-hidden md:my-28">
        <TeamSlider heading={teamHeading} teamMembers={teamMembers} />
      </section>

      <section className="my-20 md:my-28 bg-beige md:bg-white">
        <div className="container">
          <div className="flex items-center justify-between w-full py-20 text-center sm:py-28 bg-beige">
            <div className="self-start justify-start hidden -mt-6 " style={{ flex: '1 4 255px' }}>
              <StaticImage width={255} src="../assets/images/impact-hand-left.png" alt="hand" />
            </div>
            <div className="flex-auto px-6 -mt-4">
              <div className="mb-8 text-blue">
                <Image blok={careers[0]?.icon} className="mx-auto" imageClassName="w-[60px] mx-auto" />
              </div>
              <h2 className="mb-3 text-h2 lg:text-54px">{impactText[0]?.heading}</h2>
              <p className="max-w-sm mx-auto text-20px xl:max-w-full ">{impactText[0]?.content}</p>
              <div className="mx-auto md:max-w-[48rem]">
                <div className="flex flex-wrap justify-center mt-12 -ml-6 ">
                  {impactColumns.map((item, i) => (
                    <div key={`impact${i}`} className="w-full pl-6 mb-5 md:w-auto md:flex-auto">
                      <div className="font-bold">{item.subHeading}</div>
                      <div>{item.heading}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="self-end justify-end hidden -mb-6 " style={{ flex: '1 4 255px' }}>
              <StaticImage width={200} src="../assets/images/impact-hand-right.png" alt="hand" />
            </div>
          </div>
        </div>
      </section>

      <section className="my-20 md:my-32">
        <div className="container">
          <header className="mb-12 md:mb-16">
            <h2 className="text-center text-h2">{pressText[0]?.heading}</h2>
          </header>

          <div className="grid gap-10 md:grid-cols-2">
            {pressPreviews.map((p, i) => (
              <PressPreview post={p} key={`about-press-${i}`} className="pt-10 border-t border-[#B3B3B3]" />
            ))}
          </div>
        </div>
      </section>

      <CompanyBottomNav items={buttons} />
    </Layout>
  );
};

export default AboutPage;

export const query = graphql`
  query AboutQuery($id: String!) {
    storyblokEntry(id: { eq: $id }) {
      id
      name
      content
      lang
      slug
      uuid
      full_slug
    }
  }
`;
